import moment from 'moment';
import type { EddDate } from 'interfaces/edd.interface';
import { generateWithTimezone } from 'utils/datetime.utils';

interface FormatDeliveryDateParams {
  startDate: EddDate;
  endDate?: EddDate | null;
  timezone: string;
  dateDisplayFormat: string;
  timeDisplayFormat: string;
}

/**
 * Returns true when `time` falls within the reasonable delivery window (08:00–21:00:59 local).
 * Callers are responsible for ensuring `time` is a non-empty string before calling this function.
 */
export const isReasonableDeliveryHour = (
  time: string,
  timezone: string,
): boolean => {
  if (!time) return false;
  const timezoneAwareDate = generateWithTimezone(time, timezone);
  const hour = timezoneAwareDate.hour();
  const minute = timezoneAwareDate.minute();
  // Window: 08:00:00–21:00:59 inclusive. .hour() returns integer 0-23.
  // Upper bound: hour 21 is only allowed when minute === 0 (i.e., 21:00:xx).
  // 21:01+ falls outside the window.
  return hour >= 8 && (hour < 21 || (hour === 21 && minute === 0));
};

export const shouldShowTimeDisplay = (eddTime: moment.MomentInput): boolean => {
  // When the times in utc/browser_timezone are both 00:00 or 23:59, hide the timestamp and show only the date.
  const localTimeStr = moment(eddTime).format('HH:mm');
  const utcTimeStr = moment.utc(eddTime).format('HH:mm');

  const isNormalLocalTime =
    localTimeStr !== '00:00' && localTimeStr !== '23:59';
  const isNormalUtcTime = utcTimeStr !== '00:00' && utcTimeStr !== '23:59';

  return isNormalLocalTime && isNormalUtcTime;
};

export const isSameDate = (
  date1: moment.Moment,
  date2: moment.Moment,
): boolean => {
  return date1.format('YYYY-MM-DD') === date2.format('YYYY-MM-DD');
};

export const isSameTime = (
  date1: moment.Moment,
  date2: moment.Moment,
): boolean => {
  return date1.format('HH:mm') === date2.format('HH:mm');
};

export const isTimeRange = (
  startDate: EddDate,
  endDate: EddDate | null | undefined,
): boolean => {
  return !!(endDate && endDate.time !== startDate.time);
};

export const formatWithoutTime = (
  datetime: moment.Moment,
  format: string | undefined,
): string => {
  // If format is undefined or empty, return the default date format
  if (!format) {
    return datetime.format('ddd DD MMM');
  }

  // Remove all separators from format string before passing to moment
  // Separators: 'at', ',', '.', '-', space
  const cleanFormat = format.replace(/(?:\s*at|[,.-])\s*$/, '');
  // Format the date with clean format string
  return datetime.format(cleanFormat).trim();
};

export const formatTimeOnly = (
  date: moment.Moment,
  timeFormat: string,
): string => {
  return date.format(timeFormat);
};

export const formatDateTime = (
  datetime: moment.Moment,
  dateFormat: string | undefined,
  timeFormat: string | undefined,
): string => {
  // If both dateFormat and timeFormat are undefined or empty, return the date without time
  if (!dateFormat && !timeFormat) {
    return formatWithoutTime(datetime, dateFormat);
  }

  // If timeFormat is undefined or empty, return the date without time
  if (!timeFormat) {
    return formatWithoutTime(datetime, dateFormat);
  }

  // If dateFormat is undefined or empty, but have timeFormat, return the time
  if (!dateFormat) {
    return formatWithoutTime(datetime, timeFormat);
  }

  // Trim any spaces from both formats
  const cleanDateFormat = dateFormat.trim();
  const cleanTimeFormat = timeFormat.trim();

  // Format date and time separately
  const formattedDate = datetime.format(cleanDateFormat);
  const formattedTime = datetime.format(cleanTimeFormat);

  return `${formattedDate} ${formattedTime}`;
};

export const formatDeliveryDate = ({
  startDate,
  endDate,
  timezone,
  dateDisplayFormat,
  timeDisplayFormat,
}: FormatDeliveryDateParams): string => {
  const startDateTime = generateWithTimezone(startDate.time, timezone);
  const endDateTime = endDate?.time
    ? generateWithTimezone(endDate.time, timezone)
    : null;
  const shouldShowStartTime = shouldShowTimeDisplay(startDateTime);
  const shouldShowEndTime = endDateTime
    ? shouldShowTimeDisplay(endDateTime)
    : false;

  // If timeDisplayFormat is empty/falsy, never show time
  const hasTimeFormat = Boolean(timeDisplayFormat);

  const shouldShowTime =
    (shouldShowStartTime || shouldShowEndTime) && hasTimeFormat;

  // Only show time formats if shouldShowTime is true and timeDisplayFormat is provided
  // No time format means time format should be set to empty string
  const timeFormat = shouldShowTime ? timeDisplayFormat : '';

  // UC1: If the EDD is a single date with no end date, show start date
  if (!endDateTime) {
    return formatDateTime(startDateTime, dateDisplayFormat, timeFormat);
  }

  // UC2a: If dates are different, show full range
  if (!isSameDate(startDateTime, endDateTime)) {
    const startDateFormatted = formatDateTime(
      startDateTime,
      dateDisplayFormat,
      timeFormat,
    );
    const endDateFormatted = formatDateTime(
      endDateTime,
      dateDisplayFormat,
      timeFormat,
    );
    return `${startDateFormatted} - ${endDateFormatted}`;
  }

  // UC2b: If dates are same but times are different, show date once with time range
  // But if dates are the same, show date once with no time range
  if (!isSameTime(startDateTime, endDateTime)) {
    const startDateFormatted = formatDateTime(
      startDateTime,
      dateDisplayFormat,
      timeFormat,
    );

    // If start time contains 00:00 or 23:59 AND if time format is empty, hide time.
    if (!shouldShowTime) {
      return `${startDateFormatted}`;
    }

    // If start and end date is the same, the result will be sth like "20 Mar 2024, 07:30 - 09:30"
    const endTimeFormatted = formatTimeOnly(endDateTime, timeFormat);
    return `${startDateFormatted} - ${endTimeFormatted}`;
  }

  // UC2c: If both date and time are same, show single datetime
  const formattedDate = formatDateTime(
    startDateTime,
    dateDisplayFormat,
    timeFormat,
  );
  return formattedDate;
};
